import { Config } from '@/config';
import router from '@/router';
import apiService from '@/services/api.service';
import type {
  ProjectModuleDto,
  ProjectModuleTextImageConfigDto,
  ProjectModuleVideoConfigDto,
  ProjectModuleConsultationConfigDto,
  ProjectModuleAttachmentsConfigDto,
  ProjectModuleFaqConfigDto,
  ProjectModuleSubscriptionConfigDto,
  ProjectModuleTimelineConfigDto,
  ProjectModuleContactListConfigDto,
  Locales,
  ProjectModuleHeroConfigDto,
  ProjectModuleEventConfigDto,
  ProjectModuleFeedbackConfigDto,
  ProjectModuleQuestionAnswerConfigDto,
  ProjectModuleNewsConfigDto,
  ProjectModuleMapConfigDto,
  ProjectModuleDocumentSubmissionConfigDto,
  ProjectModuleConfigCTAButtonDto,
} from '@/services/types/api.types';
import { defineStore } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useProjectStore } from './projects.store';
import { usePagesStore } from './pages.store';
import { useCommunityStore } from './community.store';
import { useConfigurationStore } from './configurations.store';
import * as yup from 'yup';
import { localizableString } from '@/helpers/validation.helper';
import { Constants } from '@/constants';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import { FileUploaderV2 } from '@/providers/file-uploaders/file-uploader.v2';
import { ProcessorType, type SavingOptions } from '@/services/types/file-uploader.types';
import type { FileInfo } from '@/services/types/file.types';
import { ModuleTypeEnum, type BlockParent, type BlockRouteFor, type ModuleTypeOption } from '@/types/modules';
import { createCtaButtonsValidationSchema } from '@/helpers/cta-buttons-validation.helper';
import { DEFAULT_MAP_STYLE } from '@/helpers/map-style';
import { insertOrUpdate } from '@/helpers/store.helper';
import { blockParentOf } from '@/helpers/block.helper';

export interface BlocksState {
  modules: ProjectModuleDto[];
  itemLoadingStates: Set<number>;
}

/** Where a parent's blocks are listed. */
function blocksUrl(parent: BlockParent): string {
  switch (parent.type) {
    case Constants.Block.ParentType.Project:
      return `${Config.api.v2}/projects/${parent.id}/modules`;
    case Constants.Block.ParentType.Page:
      return `${Config.api.v2}/pages/${parent.id}/blocks`;
    default: {
      const unknownType: never = parent.type;
      throw new Error(`Unknown block parent type: ${unknownType}`);
    }
  }
}

/** Where one of a parent's blocks is read, updated and deleted. */
function blockUrl(parent: BlockParent, blockId: number): string {
  switch (parent.type) {
    case Constants.Block.ParentType.Project:
      return `${Config.api.v2}/project-modules/${blockId}`;
    case Constants.Block.ParentType.Page:
      return `${Config.api.v2}/pages/${parent.id}/blocks/${blockId}`;
    default: {
      const unknownType: never = parent.type;
      throw new Error(`Unknown block parent type: ${unknownType}`);
    }
  }
}

/** Posts a new block to the route its parent creates blocks on. */
function postBlock(block: ProjectModuleDto) {
  const parent = blockParentOf(block);
  switch (parent.type) {
    // Either API writes only the block's own columns, taking the parent from `project_id` or from the URL, so the form
    // goes as it is.
    case Constants.Block.ParentType.Project:
      return apiService.post<ProjectModuleDto>(`${Config.api.v2}/project-modules`, block);
    case Constants.Block.ParentType.Page:
      return apiService.post<ProjectModuleDto>(blocksUrl(parent), block);
    default: {
      const unknownType: never = parent.type;
      throw new Error(`Unknown block parent type: ${unknownType}`);
    }
  }
}

/**
 * Where a page block previews: at its anchor on its page, which for the homepage is the community's root. A page that
 * has no slug and is not the homepage has no address to preview at.
 */
function pageBlockPreviewURL(block: ProjectModuleDto): string | undefined {
  return usePagesStore().previewURL(block.parent_id, `#page-block-${block.id}`);
}

function getTypes(): ModuleTypeOption[] {
  const { t } = useI18n();
  const types: ModuleTypeOption[] = [
    { value: ModuleTypeEnum.Text, label: t('form.dialogs.module.types.text-image') },
    { value: ModuleTypeEnum.Video, label: t('form.dialogs.module.types.video') },
    { value: ModuleTypeEnum.Consultation, label: t('form.dialogs.module.types.consultation') },
    { value: ModuleTypeEnum.Attachments, label: t('form.dialogs.module.types.attachments') },
    { value: ModuleTypeEnum.Faq, label: t('form.dialogs.module.types.faq') },
    { value: ModuleTypeEnum.Subscription, label: t('form.dialogs.module.types.subscription') },
    { value: ModuleTypeEnum.Timeline, label: t('form.dialogs.module.types.timeline') },
    { value: ModuleTypeEnum.ContactList, label: t('form.dialogs.module.types.contactList') },
    { value: ModuleTypeEnum.Hero, label: t('form.dialogs.module.types.hero') },
    { value: ModuleTypeEnum.Event, label: t('form.dialogs.module.types.event') },
    { value: ModuleTypeEnum.Feedback, label: t('form.dialogs.module.types.feedback') },
    { value: ModuleTypeEnum.News, label: t('form.dialogs.module.types.news') },
    { value: ModuleTypeEnum.QuestionAnswer, label: t('form.dialogs.module.types.questionAnswer') },
    { value: ModuleTypeEnum.Map, label: t('form.dialogs.module.types.map') },
    { value: ModuleTypeEnum.DocumentSubmission, label: t('form.dialogs.module.types.documentSubmission') },
  ];
  return types;
}

function createEmptyCtaButton(): ProjectModuleConfigCTAButtonDto {
  return { enabled: false, text: { en: '', fr: '' }, linkType: 'url', url: { en: '', fr: '' }, moduleId: undefined };
}

/** The pair of disabled buttons a new module starts with, for every type that offers CTA buttons. */
function createEmptyCtaButtons() {
  return { button1: createEmptyCtaButton(), button2: createEmptyCtaButton() };
}

function getImageAlignmentOptions(): any[] {
  const { t } = useI18n();
  return [
    { value: 1, label: t('global.alignment.left') },
    { value: 2, label: t('global.alignment.right') },
    { value: 3, label: t('global.alignment.center') },
  ];
}

function getVideoAlignmentOptions(): any[] {
  const { t } = useI18n();
  return [
    { value: 1, label: t('global.alignment.left') },
    { value: 2, label: t('global.alignment.right') },
    { value: 3, label: t('global.alignment.center') },
  ];
}

const findModule = (modules: ProjectModuleDto[], moduleId: number) => {
  return modules.find((x) => {
    return x.id == moduleId;
  });
};

export const useBlocksStore = defineStore({
  id: 'blocks',
  state: (): BlocksState => ({
    modules: [],
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (moduleId: number) => state.itemLoadingStates.has(moduleId),
    getItem: (state) => (moduleId: number) => state.modules.find((s) => s.id === moduleId),
    /** A parent's blocks, in their order. A page and a project can share an id, so both the type and the id must match. */
    getBlocksByParent: (state) => (parent: BlockParent) =>
      state.modules
        .filter((block) => block.parent_type === parent.type && block.parent_id === parent.id)
        .sort((a, b) => a.order_num - b.order_num),
    getTypes: (state) => getTypes,
    getImageAlignmentOptions: (state) => getImageAlignmentOptions,
    getVideoAlignmentOptions: (state) => getVideoAlignmentOptions,
    previewURL: (state) => (moduleId: number) => {
      const module = findModule(state.modules, moduleId);
      const projectStore = useProjectStore();
      const communityStore = useCommunityStore();
      if (module?.parent_type === Constants.Block.ParentType.Page) return pageBlockPreviewURL(module);
      if (module) {
        const project = projectStore.getItem(module.parent_id);
        if (!project) return null;
        const communityUrl = communityStore.previewURL(project.community_id);
        return `${communityUrl}/projects/${project.slug}#toc_${module.parent_id}_${module.id}`;
      }
    },
    /**
     * Returns a Yup validation schema for a module's `config` object based on its type.
     *
     * This is used to dynamically validate module-specific configuration (e.g. CTA buttons,
     * hero settings, etc.) while respecting localization and conditional logic (enabled/disabled).
     *
     * @param state - The current store state (required by Vuex/Pinia mapper convention)
     * @param payload - Object containing the current form values and context
     * @param payload.value - The current form values (entire form object). Used by `.when()` to read sibling fields like `enabled` or `linkType`
     * @param payload.block_type - The type ID of the module (from Constants.Block.Type)
     * @param payload.languageEnabled - Array of enabled locales (e.g. ['fr', 'en']) — passed to `localizableString()`
     *
     * @returns {import('yup').ObjectSchema} A Yup schema for `config` (or nested parts like `config.ctaButtons`)
     *          Returns `yup.object().nullable()` for unsupported module types.
     *
     * @example
     * // In vee-validate form:
     * validationSchema: computed(() => ({
     *   config: getModuleConfigValidationSchema(getters)({ value: values, block_type: values.block_type, languageEnabled })
     * }))
     */
    getModuleConfigValidationSchema:
      (state) =>
      ({ value, block_type, languageEnabled }: { value: any; block_type: number; languageEnabled: Locales[] }) => {
        const ctaButtonsSchema = createCtaButtonsValidationSchema(languageEnabled, { includeSecondaryButton: true });

        switch (block_type) {
          case Constants.Block.Type.Text:
          case Constants.Block.Type.Video:
            return yup.object().shape({
              ctaButtons: ctaButtonsSchema,
            });
          case Constants.Block.Type.Hero:
            return yup.object().shape({
              ctaButtons: ctaButtonsSchema,
              // image: yup.mixed().when('displayMode', {
              //   is: 'image',
              //   then: () =>
              //     yup
              //       .object({
              //         url: yup.string().required(),
              //         color: yup.string().optional(),
              //         altText: localizableString(languageEnabled, { optional: true }),
              //       })
              //       .transform((value) => (value === '' ? undefined : value))
              //       .nullable(),
              //   otherwise: () => yup.mixed().nullable().oneOf([null, '']),
              // }),
              video: yup.object().when('displayMode', {
                is: 'video',
                then: () => localizableString(languageEnabled),
                otherwise: (schema) => schema.nullable(),
              }),
              videoType: yup.string().when('displayMode', {
                is: 'video',
                then: () => yup.string().required(),
                otherwise: (schema) => schema.nullable(),
              }),
            });
          case Constants.Block.Type.Feedback:
            return yup.object().shape({
              description: localizableString(languageEnabled, { optional: true }),
              thankYouMessage: localizableString(languageEnabled, { optional: true }),
              maxLength: yup.number().nullable().positive('Must be a positive number').integer('Must be an integer'),
              anonymousFeedback: yup.boolean().optional(),
            });
          case Constants.Block.Type.QuestionAnswer:
            return yup.object().shape({
              description: localizableString(languageEnabled, { optional: true }),
              thankYouMessage: localizableString(languageEnabled, { optional: true }),
              noAnswersMessage: localizableString(languageEnabled, { optional: true }),
              maxLength: yup.number().nullable().positive('Must be a positive number').integer('Must be an integer'),
            });
          case Constants.Block.Type.Map:
            // `bounds`, `zoom` and `layers` are deliberately absent: they are written by the
            // boundary map and by the layer endpoints, never typed into a field, so a rule here
            // could only ever fail on a path the admin cannot correct.
            return yup.object().shape({
              ctaButtons: ctaButtonsSchema,
              description: localizableString(languageEnabled, { optional: true }),
            });
          case Constants.Block.Type.DocumentSubmission:
            return yup.object().shape({
              description: localizableString(languageEnabled, { optional: true }),
              submitButtonLabel: localizableString(languageEnabled, { optional: true }),
              thankYouMessage: localizableString(languageEnabled, { optional: true }),
            });
          default:
            return yup.object().nullable();
        }
      },
  },
  actions: {
    async fetchModuleById(projectModuleId: number, parent: BlockParent) {
      this.itemLoadingStates.add(projectModuleId);
      try {
        const { data } = await apiService.get<ProjectModuleDto>(blockUrl(parent, projectModuleId));
        insertOrUpdate(this.modules, data, 'id');
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(projectModuleId);
      }
    },
    async fetchBlocks(parent: BlockParent) {
      try {
        const { data } = await apiService.get<ProjectModuleDto[]>(blocksUrl(parent));
        this.modules = data;
        for (const module of data) {
          insertOrUpdate(this.modules, module, 'id');
        }
      } catch (error) {
        console.error(error);
      }
    },
    createModule(parent: BlockParent): ProjectModuleDto {
      return {
        parent_type: parent.type,
        parent_id: parent.id,
        project_id: parent.type === Constants.Block.ParentType.Project ? parent.id : null,
        block_type: Constants.Block.Type.Text,
        order_num: -1,
        title: { en: '', fr: '' },
        config: { highlight: false, includeInTableOfContents: false },
      };
    },
    createTextImageModuleConfig(): ProjectModuleTextImageConfigDto {
      return {
        highlight: false,
        includeInTableOfContents: false,
        content: { en: '', fr: '' },
        hasImage: false,
        image: null,
        imageAlignment: 1,
        imageCaption: { en: '', fr: '' },
        ctaButtons: createEmptyCtaButtons(),
      };
    },
    createVideoModuleConfig(): ProjectModuleVideoConfigDto {
      return {
        highlight: false,
        includeInTableOfContents: false,
        content: { en: '', fr: '' },
        videoType: '',
        video: { en: '', fr: '' },
        videoAlignment: 1,
        videoCaption: { en: '', fr: '' },
        ctaButtons: createEmptyCtaButtons(),
      };
    },
    createConsultationModuleConfig(consultation_id: number): ProjectModuleConsultationConfigDto {
      return {
        highlight: true,
        includeInTableOfContents: false,
        consultationId: consultation_id,
        useConsultationBanner: true,
        image: null,
      };
    },
    createAttachmentsModuleConfig(): ProjectModuleAttachmentsConfigDto {
      return { highlight: false, includeInTableOfContents: false };
    },
    createFaqModuleConfig(): ProjectModuleFaqConfigDto {
      return { highlight: false, includeInTableOfContents: false };
    },
    createSubscriptionModuleConfig(): ProjectModuleSubscriptionConfigDto {
      return { highlight: true, includeInTableOfContents: false };
    },
    createTimelineModuleConfig(): ProjectModuleTimelineConfigDto {
      return { highlight: false, includeInTableOfContents: false };
    },
    createContactListModuleConfig(): ProjectModuleContactListConfigDto {
      return { highlight: false, includeInTableOfContents: false, description: { en: '', fr: '' } };
    },
    createHeroModuleConfig(): ProjectModuleHeroConfigDto {
      return {
        useProjectTitle: false,
        useProjectImage: false,
        highlight: false,
        includeInTableOfContents: false,
        displayMode: 'image',
        image: null,
        videoType: '',
        video: { en: '', fr: '' },
        videoCaption: { en: '', fr: '' },
        layout: 1,
        overlay: {
          backgroundColor: '',
          backgroundOpacity: 0.5,
          alignment: 1,
          textColor: '#ffffff',
          description: { en: '', fr: '' },
        },
        ctaButtons: createEmptyCtaButtons(),
      };
    },
    createEventModuleConfig(): ProjectModuleEventConfigDto {
      return { highlight: false, includeInTableOfContents: false, content: { en: '', fr: '' } };
    },
    createFeedbackModuleConfig(): ProjectModuleFeedbackConfigDto {
      return {
        anonymousFeedback: false,
        description: { en: '', fr: '' },
        highlight: false,
        includeInTableOfContents: false,
        maxLength: null,
        requiresModeration: false,
        thankYouMessage: { en: '', fr: '' },
      };
    },
    createQuestionAnswerModuleConfig(): ProjectModuleQuestionAnswerConfigDto {
      return {
        description: { en: '', fr: '' },
        highlight: false,
        includeInTableOfContents: false,
        maxLength: 500,
        thankYouMessage: { en: '', fr: '' },
        noAnswersMessage: { en: 'No public questions yet.', fr: 'Aucune question publique pour le moment.' },
      };
    },
    createNewsModuleConfig(): ProjectModuleNewsConfigDto {
      return { highlight: false, includeInTableOfContents: false, content: { en: '', fr: '' } };
    },
    createDocumentSubmissionModuleConfig(): ProjectModuleDocumentSubmissionConfigDto {
      return {
        highlight: false,
        includeInTableOfContents: false,
        description: { en: '', fr: '' },
        submitButtonLabel: { en: '', fr: '' },
        thankYouMessage: { en: '', fr: '' },
      };
    },
    createMapModuleConfig(): ProjectModuleMapConfigDto {
      return {
        highlight: false,
        includeInTableOfContents: false,
        description: { en: '', fr: '' },
        // The boundary map falls back to the same style, so a module saved without opening the
        // style select still renders the map the admin was shown.
        mapStyle: DEFAULT_MAP_STYLE,
        // No framed area until the admin sets one from the boundary map. The web renderer checks
        // for both corners before it mounts a map at all, so null here is what keeps a
        // half-configured module from rendering an empty canvas on the project page.
        bounds: null,
        zoom: null,
        layers: [],
        hideAddressSearch: false,
        ctaButtons: createEmptyCtaButtons(),
      };
    },
    async deleteModule(module: ProjectModuleDto) {
      try {
        const parent = blockParentOf(module);
        await apiService.delete(blockUrl(parent, module.id!));
        await this.fetchBlocks(parent);
      } catch (error) {
        console.error(error);
      }
    },
    /** A create opens the new block on the route `blockRouteFor` builds for it; an update stays where it is. */
    async saveModule(module: ProjectModuleDto, blockRouteFor?: BlockRouteFor): Promise<boolean> {
      try {
        const parent = blockParentOf(module);
        let moduleId = module.id;
        if (moduleId) {
          await apiService.put<ProjectModuleDto>(blockUrl(parent, moduleId), module);
        } else {
          const { data } = await postBlock(module);
          moduleId = data.id;
          if (blockRouteFor) router.push(blockRouteFor(moduleId!));
        }
        await this.fetchModuleById(moduleId!, parent);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async updateModules(...modules: ProjectModuleDto[]) {
      try {
        for (const module of modules) {
          const response = await apiService.put(blockUrl(blockParentOf(module), module.id!), module);
          // Handle the response, e.g., updating the state, calling mutations, etc.
        }
      } catch (error) {
        console.error(error);
      }
    },
    /**
     * Uploads a page block's image to `/files` and answers the `FileInfo` it stored, for the form to hold until the
     * block's Save stores it: a page block has no image route.
     */
    async uploadPageBlockImage(options: SavingOptions): Promise<FileInfo | undefined> {
      try {
        const { data } = await new FileUploaderV2('v2').upload({
          files: options.files,
          metadata: options.metadata,
          url: `${Config.api.v2}/files`,
        });
        return data.filesInfo[0];
      } catch (error) {
        console.error(error);
        return undefined;
      }
    },
    // The image and map-layer routes exist for project blocks only. Each of these takes the block, not
    // its id, to refetch it from its parent.
    async saveModuleImage(module: ProjectModuleDto, options: SavingOptions): Promise<boolean> {
      try {
        await FileUploaderFactory.create(options.version).upload({
          files: options.files,
          metadata: options.metadata,
          url: `${Config.api.v2}/project-modules/${module.id}/image`,
        });
        await this.fetchModuleById(module.id!, blockParentOf(module));
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async removeModuleImage(module: ProjectModuleDto) {
      try {
        await apiService.delete(`${Config.api.v2}/project-modules/${module.id}/image`);
        await this.fetchModuleById(module.id!, blockParentOf(module));
      } catch (error) {
        console.error(error);
      }
    },
    /**
     * Imports a GeoJSON file as a layer on a Map module.
     *
     * The layer is created server-side and written straight into the module's stored config, so
     * this refetches rather than patching the form: the response carries an id and a computed
     * bounding box that only the API can produce. Mirrors `formsStore.uploadMapLayer`, which does
     * the same for the consultation map step against its own endpoint.
     */
    async uploadMapLayer(module: ProjectModuleDto, options: SavingOptions): Promise<boolean> {
      try {
        await FileUploaderFactory.create(options.version).upload({
          file: options.file,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/project-modules/${module.id}/map-layers`,
        });
        await this.fetchModuleById(module.id!, blockParentOf(module));
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteMapLayer(module: ProjectModuleDto, layerId: number): Promise<boolean> {
      try {
        await apiService.delete(`${Config.api.v2}/project-modules/${module.id}/map-layers/${layerId}`);
        await this.fetchModuleById(module.id!, blockParentOf(module));
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
  },
});
